import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const listenersRef = useRef(new Map()); // type -> Set<callback>

  const getWsUrl = () => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const base = BACKEND_URL.replace(/^http/, 'ws');
    return `${base}/api/ws/${token}`;
  };

  const connect = useCallback(() => {
    const url = getWsUrl();
    if (!url || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        if (event.data === 'pong') return;
        try {
          const data = JSON.parse(event.data);
          const type = data.type;
          // Handle presence events internally
          if (type === 'presence') {
            setOnlineUsers(prev => {
              const next = new Set(prev);
              if (data.status === 'online') next.add(data.user_id);
              else next.delete(data.user_id);
              return next;
            });
          }
          // Dispatch to all listeners for this type
          const callbacks = listenersRef.current.get(type);
          if (callbacks) {
            callbacks.forEach(cb => cb(data));
          }
          // Also dispatch to wildcard listeners
          const wildcards = listenersRef.current.get('*');
          if (wildcards) {
            wildcards.forEach(cb => cb(data));
          }
        } catch (e) {
          // ignore
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        wsRef.current = null;
        // Auto-reconnect after 3s
        reconnectTimerRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch (e) {
      // retry
      reconnectTimerRef.current = setTimeout(connect, 5000);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof data === 'string' ? data : JSON.stringify(data));
    }
  }, []);

  // Subscribe to a message type. Returns unsubscribe function.
  const subscribe = useCallback((type, callback) => {
    if (!listenersRef.current.has(type)) {
      listenersRef.current.set(type, new Set());
    }
    listenersRef.current.get(type).add(callback);
    return () => {
      const set = listenersRef.current.get(type);
      if (set) {
        set.delete(callback);
        if (set.size === 0) listenersRef.current.delete(type);
      }
    };
  }, []);

  // Auto-connect when user is logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) connect();

    const handleLogin = () => connect();
    const handleLogout = () => disconnect();
    window.addEventListener('userUpdated', handleLogin);
    window.addEventListener('storage', handleLogin);

    // Keep-alive ping every 30s
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send('ping');
      }
    }, 30000);

    return () => {
      disconnect();
      clearInterval(pingInterval);
      window.removeEventListener('userUpdated', handleLogin);
      window.removeEventListener('storage', handleLogin);
    };
  }, [connect, disconnect]);

  // Check online status for a batch of user IDs
  const checkOnlineStatus = useCallback(async (userIds) => {
    if (!userIds || userIds.length === 0) return;
    try {
      const resp = await fetch(`${BACKEND_URL}/api/users/online-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ user_ids: userIds }),
      });
      const data = await resp.json();
      setOnlineUsers(prev => {
        const next = new Set(prev);
        Object.entries(data).forEach(([uid, online]) => {
          if (online) next.add(uid); else next.delete(uid);
        });
        return next;
      });
    } catch (e) {
      // ignore
    }
  }, []);

  const isUserOnline = useCallback((userId) => onlineUsers.has(userId), [onlineUsers]);

  const value = { isConnected, send, subscribe, connect, disconnect, onlineUsers, isUserOnline, checkOnlineStatus };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error('useWebSocket must be used within WebSocketProvider');
  return ctx;
};

/**
 * Hook: subscribe to a specific WS event type
 * Usage: useWSEvent('chat_message', (data) => { ... })
 */
export const useWSEvent = (type, callback) => {
  const { subscribe } = useWebSocket();
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const handler = (data) => callbackRef.current(data);
    return subscribe(type, handler);
  }, [type, subscribe]);
};

export default WebSocketContext;
